import { Box, styled } from '@mui/material'
import { CardContent, EditableCardItem, SelectOption } from '../../Kanban'
import { getOpenWbsItemDetailSpec } from '../../../../../lib/functions/wbsItem'
import { memo, useCallback, useMemo } from 'react'
import { getPathByExternalId } from '../../..'
import {
  FunctionLayer,
  pushFunctionLayer,
} from '../../../../../store/functionLayer'
import { UserAvatarsGroup } from './UserAvatarsGroup'
import { useDispatch } from 'react-redux'
import TaskActualWorkDialog from '../../../../components/dialogs/TaskActualWorkDialog'
import { DisplayNameItem } from './DisplayNameItem'
import { ProjectPlanPath } from './ProjectPlanPath'
import { useDialogState } from '../../../../components/dialogs/useDialogState'
import { ProgressIcon } from '../../../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus/ProgressIcon'

export type Props = {
  content: CardContent
  onChange?: (id: string, field: EditableCardItem, changed: any) => void
  onCloseWbsItemDetail?: (wbsItemCode: string) => void
}

export const WbsStatusCardContent = memo(
  ({ content, onCloseWbsItemDetail }: Props) => {
    const dispatch = useDispatch()
    const [isOpenActulaWorkDialog, closeActulaWorkDialog] = useDialogState()

    const openDetailInLayer = useCallback(
      (layer: FunctionLayer) => {
        dispatch(
          pushFunctionLayer({
            ...layer,
            onClose: async () => {
              onCloseWbsItemDetail?.(layer.code!)
            },
          })
        )
      },
      [dispatch, onCloseWbsItemDetail]
    )

    const onClickWbsItemIcon = useCallback(
      async (event: any) => {
        const openDetailSpec = await getOpenWbsItemDetailSpec(true, content)
        const layer = openDetailSpec.layer

        if (event && (event['ctrlKey'] || event['metaKey'])) {
          window.open(`${getPathByExternalId(layer.externalId)}/${layer.code!}`)
          return
        }
        openDetailInLayer(layer)
      },
      [content, openDetailInLayer]
    )

    const accountable = useMemo(
      () =>
        content.accountable
          ? ({
              ...content.accountable,
              value: content.accountable?.uuid,
            } as SelectOption)
          : null,
      [content.accountable]
    )
    const responsible = useMemo(
      () =>
        content.responsible
          ? ({
              ...content.responsible,
              value: content.responsible?.uuid,
            } as SelectOption)
          : null,
      [content.responsible]
    )
    const assignee = useMemo(
      () =>
        content.assignee
          ? ({
              ...content.assignee,
              value: content.assignee?.uuid,
            } as SelectOption)
          : null,
      [content.assignee]
    )

    return (
      <>
        <ProjectPlanPath
          path={content.path}
          parentDisplayName={content.parentWbsItem?.displayName}
        />
        <DisplayNameItem
          value={content.displayName}
          wbsItemType={content.wbsItemType!}
          onClickIcon={onClickWbsItemIcon}
        />
        <RowArea
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <ProgressIcon wbsItem={content} fontSize={24} />
          </Box>
          <UserAvatarsGroup
            accountable={accountable}
            responsible={responsible}
            assignee={assignee}
          />
        </RowArea>
        {isOpenActulaWorkDialog && (
          <TaskActualWorkDialog
            open={true}
            taskUuid={content.uuid ?? ''}
            closeHandler={() => closeActulaWorkDialog()}
          />
        )}
      </>
    )
  },
  (prev, next) => prev.content === next.content
)

const RowArea = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
  width: '100%',
})
